import React, { useState } from 'react';
import EmptyState from './EmptyState';
import './LedenList.css';

const BIJZONDERHEDEN_PREVIEW_LENGTH = 27;

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
const normalizeStatus = (status) => (status || '').toString().trim().toLowerCase();

const getStatusBadgeClassName = (status) => {
  const normalized = normalizeStatus(status);
  const statusClassByValue = {
    echtpaar: 'status-echtpaar',
    man: 'status-man',
    priester: 'status-priester',
    seminarist: 'status-seminarist',
    vrouw: 'status-vrouw'
  };

  return statusClassByValue[normalized] || 'status-default';
};

/**
 * LedenList Component
 * 
 * Displays a table of members (leden) for a gemeenschap
 * Allows add, edit, and delete operations
 * 
 * @param {Array} leden - Array of lid objects
 * @param {Function} onAdd - Callback to add new member
 * @param {Function} onEdit - Callback to edit member
 * @param {Function} onDelete - Callback to delete member
 * @param {boolean} canEdit - Whether current user can edit (admin or verantwoordelijke)
 */
const LedenList = ({
  leden,
  onAdd,
  onEdit,
  onDelete,
  canEdit
}) => {
  const [expandedBijzonderheden, setExpandedBijzonderheden] = useState({});
  const totalAantal = leden.reduce((sum, lid) => sum + (Number(lid.aantal) || 0), 0);

  const toggleBijzonderheden = (lidId) => {
    setExpandedBijzonderheden((prev) => ({
      ...prev,
      [lidId]: !prev[lidId]
    }));
  };

  const renderBijzonderheden = (lid) => {
    const rawText = lid.bijzonderheden || '';

    if (!rawText) {
      return null;
    }

    const isExpanded = Boolean(expandedBijzonderheden[lid.id]);
    const isLongText = rawText.length > BIJZONDERHEDEN_PREVIEW_LENGTH;
    const previewText = isLongText
      ? `${rawText.slice(0, BIJZONDERHEDEN_PREVIEW_LENGTH).trimEnd()}...`
      : rawText;

    return (
      <div className="bijzonderheden-content">
        <span>{isExpanded ? rawText : previewText}</span>
        {isLongText && (
          <button
            type="button"
            className="text-link-button"
            onClick={() => toggleBijzonderheden(lid.id)}
            aria-label={`${isExpanded ? 'Verberg' : 'Toon'} bijzonderheden van ${lid.voornaam} ${lid.achternaam}`}
          >
            {isExpanded ? 'Minder tonen' : 'Meer tonen'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="leden-list-container">
      <div className="leden-header">
        <h4>Leden ({totalAantal})</h4>
        {canEdit && (
          <button
            className="button-add"
            onClick={onAdd}
            title="Nieuw lid toevoegen"
          >
            + Nieuw lid
          </button>
        )}
      </div>

      {leden.length === 0 ? (
        <EmptyState
          icon="👥"
          message="Nog geen leden"
          hint={canEdit ? 'Klik op "Nieuw lid" om leden toe te voegen' : undefined}
          actionLabel={canEdit ? 'Nieuw lid' : undefined}
          onAction={canEdit ? onAdd : undefined}
        />
      ) : (
        <div className="table-responsive">
          <table className="leden-table">
            <thead>
              <tr>
                <th className="mobile-summary-col">Lid</th>
                <th>Voornaam</th>
                <th>Achternaam</th>
                <th>Status</th>
                <th>Geboortedatum</th>
                <th>Email</th>
                <th>Telefoon</th>
                <th>Bijzonderheden</th>
                {canEdit && <th>Acties</th>}
              </tr>
            </thead>
            <tbody>
              {leden.map((lid) => (
                <tr key={lid.id}>
                  <td className="mobile-summary-cell" data-label="Lid">
                    <div className="mobile-summary-name">{`${lid.voornaam} ${lid.achternaam}`}</div>
                    <span className={`status-badge ${getStatusBadgeClassName(lid.status)}`}>{lid.status || '-'}</span>
                  </td>
                  <td data-label="Voornaam">{lid.voornaam}</td>
                  <td data-label="Achternaam">{lid.achternaam}</td>
                  <td data-label="Status">
                    <span className={`status-badge ${getStatusBadgeClassName(lid.status)}`}>{lid.status || '-'}</span>
                  </td>
                  <td
                    className={`text-muted${hasValue(lid.geboortedatum) ? '' : ' is-empty'}`}
                    data-label="Geboortedatum"
                  >
                    {hasValue(lid.geboortedatum) ? lid.geboortedatum : ''}
                  </td>
                  <td
                    className={`text-muted${hasValue(lid.email) ? '' : ' is-empty'}`}
                    data-label="Email"
                  >
                    {hasValue(lid.email) ? lid.email : ''}
                  </td>
                  <td
                    className={`text-muted${hasValue(lid.telefoon) ? '' : ' is-empty'}`}
                    data-label="Telefoon"
                  >
                    {hasValue(lid.telefoon) ? lid.telefoon : ''}
                  </td>
                  <td
                    className={`text-muted${hasValue(lid.bijzonderheden) ? '' : ' is-empty'}`}
                    data-label="Bijzonderheden"
                  >
                    {renderBijzonderheden(lid)}
                  </td>
                  {canEdit && (
                    <td className="actions" data-label="Acties">
                      <button
                        className="icon-button edit"
                        onClick={() => onEdit(lid)}
                        title="Bewerken"
                        aria-label={`Bewerk ${lid.voornaam} ${lid.achternaam}`}
                      >
                        ✎
                      </button>
                      <button
                        className="icon-button delete"
                        onClick={() => {
                          if (window.confirm(`Weet u zeker dat u ${lid.voornaam} ${lid.achternaam} wilt verwijderen?`)) {
                            onDelete(lid.id);
                          }
                        }}
                        title="Verwijderen"
                        aria-label={`Verwijder ${lid.voornaam} ${lid.achternaam}`}
                      >
                        ✕
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LedenList;
